/*
 * Botón "Generar SAR" + modal con el informe SAR estructurado.
 * Se le pasa {lat, lng, address, height_m?, site_id?, site_name?}.
 * - Pulsa → POST /api/research/sar (Gemini rellena las 8 secciones).
 * - Muestra el resultado en tarjetas fieles al template Totem.
 * - Botón "Descargar PDF" enlaza a /api/research/sar/{id}/pdf.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../api/client';
import { FileText, Loader2, Download, X, ShieldCheck, Camera, Trash2, Image as ImageIcon, PenLine, Building } from 'lucide-react';
import SARSignatureModal from './SARSignatureModal';
import OperatorSelect from './OperatorSelect';

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 border-t border-white/5">
    <div className="text-white/50 text-xs font-bold">{k}</div>
    <div className="text-white text-xs">{v || <span className="text-white/30">—</span>}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
    <div className="text-[10px] font-black tracking-widest text-[#A3FF12] mb-2">{title}</div>
    <div>{children}</div>
  </div>
);

const UrbanisticSection = ({ sar, onRefreshed }) => {
  const [refreshing, setRefreshing] = React.useState(false);
  const urb = sar.urbanistic_determinations || {};
  const viewer = urb.viewer || null;
  const hasData = urb && !urb.error && (urb.plan_general || urb.calificacion_ordenanza || urb.altura_maxima);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/research/sar/${sar.id}/refresh-urbanistic`);
      onRefreshed(data);
    } catch (e) {
      alert('Error consultando urbanismo: ' + (e.response?.data?.detail || e.message));
    } finally { setRefreshing(false); }
  };

  const list = (v) => Array.isArray(v) ? v.filter(Boolean).join(' · ') : (v || null);
  const ord = urb.ordenanza_telecom || {};

  return (
    <Section title="5.b · Determinaciones urbanísticas municipales (PGOU / ordenanza)">
      {hasData ? (
        <div>
          <Row k="Plan general" v={urb.plan_general}/>
          <Row k="Clasificación suelo" v={urb.clasificacion_suelo}/>
          <Row k="Calificación / ordenanza" v={urb.calificacion_ordenanza}/>
          <Row k="Usos permitidos" v={list(urb.usos_permitidos)}/>
          <Row k="Altura máxima" v={urb.altura_maxima}/>
          <Row k="Edificabilidad" v={urb.edificabilidad}/>
          <Row k="Ocupación máxima" v={urb.ocupacion_maxima}/>
          <Row k="Retranqueos" v={urb.retranqueos}/>
          <Row k="Condiciones telecom." v={urb.condiciones_telecomunicaciones}/>
          <Row k="Servidumbres / afecciones" v={urb.servidumbres_afecciones}/>
          <Row k="Normativa municipal" v={list(urb.normativa_municipal)}/>
          <Row k="Normativa autonómica" v={list(urb.normativa_autonomica)}/>
          <Row k="Normativa estatal" v={list(urb.normativa_estatal)}/>
          <Row k="Régimen previsto" v={urb.tramite_previsto}/>
          <Row k="Notas críticas" v={urb.notas_criticas}/>
          <Row k="Confianza IA" v={(urb.confianza || '—').toUpperCase()}/>

          {ord && Object.keys(ord).length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="text-white/70 text-[11px] font-black uppercase tracking-wider mb-2">
                Ordenanza municipal de telecomunicaciones
              </div>
              <Row k="¿Existe ordenanza?" v={ord.existe}/>
              <Row k="Nombre" v={ord.nombre}/>
              <Row k="Año" v={ord.ano}/>
              <Row k="Artículos clave" v={list(ord.articulos_clave)}/>
              <Row k="Plan despliegue obligat." v={ord.obligacion_plan_despliegue}/>
              <Row k="Seguro RC obligat." v={ord.obligacion_seguro_rc}/>
              <Row k="Cert. emisiones obligat." v={ord.obligacion_certificado_emisiones}/>
              <Row k="Prohibiciones" v={list(ord.prohibiciones)}/>
              <Row k="Dist. mín. viviendas (m)" v={ord.distancia_minima_viviendas_m}/>
              <Row k="Alt. máx. sobre cubierta (m)" v={ord.altura_maxima_sobre_cubierta_m}/>
              <Row k="Mimetización obligat." v={ord.mimetizacion_obligatoria}/>
              <Row k="Sanción máx." v={ord.sancion_maxima_eur}/>
              {ord.url_bop_o_boletin && (
                <div className="mt-2">
                  <a href={ord.url_bop_o_boletin} target="_blank" rel="noopener noreferrer"
                     className="text-[#A3FF12] text-xs underline hover:text-[#b8ff3a]">
                    Ver ordenanza en boletín oficial ↗
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-2">
          <div className="text-orange-300 text-xs font-black mb-1">
            {urb.error ? 'Adquiriam no disponible' : 'Determinaciones no calculadas'}
          </div>
          <div className="text-white/70 text-xs">
            {urb.error || 'Este SAR se generó antes de que la sección urbanística estuviese disponible. Púlsa "Recalcular" para pedir las determinaciones a Adquiriam.'}
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-white/5">
        {viewer?.url && (
          <a href={viewer.url} target="_blank" rel="noopener noreferrer"
             data-testid="link-urban-viewer"
             className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-white/10">
            <Building size={14}/>
            {viewer.matched ? 'Abrir visor urbanístico oficial' : 'Buscar visor urbanístico'}
          </a>
        )}
        <button onClick={refresh} disabled={refreshing}
          data-testid="btn-refresh-urban"
          className="ml-auto bg-[#A3FF12] text-black font-black text-[11px] px-3 py-1.5 rounded-lg hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center gap-1">
          {refreshing ? <Loader2 className="animate-spin" size={12}/> : null}
          {refreshing ? 'Consultando…' : 'Recalcular urbanismo'}
        </button>
      </div>
      {hasData && (
        <div className="text-white/40 text-[10px] mt-2 italic">
          {urb.disclaimer || 'Valores orientativos. Verifica en el visor urbanístico antes de firmar contrato.'}
        </div>
      )}
    </Section>
  );
};

const SARModal = ({ sar: initialSar, onClose }) => {
  const [sar, setSar] = useState(initialSar);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(null); // category being uploaded
  const [signing, setSigning] = useState(false);
  const [editingOperator, setEditingOperator] = useState(false);
  const [operatorSaving, setOperatorSaving] = useState(false);
  const [refreshingCat, setRefreshingCat] = useState(false);
  useEffect(() => { setSar(initialSar); }, [initialSar]);

  useEffect(() => {
    api.get('/research/sar/photo-categories').then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
  }, []);

  const saveOperator = async (op) => {
    setOperatorSaving(true);
    try {
      const { data } = await api.put(`/research/sar/${sar.id}/operator`, op || {});
      setSar(data);
      setEditingOperator(false);
    } catch (e) {
      alert('Error guardando operador: ' + (e.response?.data?.detail || e.message));
    } finally { setOperatorSaving(false); }
  };

  const refreshCatastro = async () => {
    setRefreshingCat(true);
    try {
      const { data } = await api.post(`/research/sar/${sar.id}/refresh-catastro`);
      setSar(data);
      const cat = data.catastro || {};
      if (cat.reference) {
        alert(`Catastro actualizado: ${cat.reference}${cat.map_png_b64 ? ' + mapa WMS' : ''}`);
      } else {
        alert('Catastro no disponible ahora mismo: ' + (cat.error || 'reintenta más tarde'));
      }
    } catch (e) {
      alert('Error consultando Catastro: ' + (e.response?.data?.detail || e.message));
    } finally { setRefreshingCat(false); }
  };

  if (!sar) return null;
  const s1 = sar.sec1_localizacion || {};
  const s2 = sar.sec2_adquisicion || {};
  const s3 = sar.sec3_transmision || {};
  const s4 = sar.sec4_obra_civil || {};
  const s5 = sar.sec5_legalizar || {};
  const s6 = sar.sec6_zona_trabajada || {};
  const s7 = sar.sec7_simulaciones || {};
  const s8 = sar.sec8_informe_fotografico || {};
  const cu = s5.condiciones_urbanisticas || {};
  const photos = sar.photos || [];

  const reloadSar = async () => {
    try {
      const { data } = await api.get(`/research/sar/${sar.id}`);
      setSar(data);
    } catch { /* ignore */ }
  };

  const uploadPhoto = async (categoryId, file, caption = '') => {
    if (!file) return;
    setUploading(categoryId);
    try {
      const fd = new FormData();
      fd.append('category', categoryId);
      fd.append('caption', caption);
      fd.append('file', file);
      await api.post(`/research/sar/${sar.id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await reloadSar();
    } catch (e) {
      alert('Error subiendo foto: ' + (e.response?.data?.detail || e.message));
    } finally { setUploading(null); }
  };

  const deletePhoto = async (photoId) => {
    if (!confirm('¿Eliminar esta foto?')) return;
    try {
      await api.delete(`/research/sar/${sar.id}/photos/${photoId}`);
      await reloadSar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadPdf = async () => {
    try {
      // Usa el cliente axios (interceptor añade Authorization con token fresco
      // + refresh en 401). El fetch nativo leía el token snapshotado en render,
      // que en producción salía caducado si el modal llevaba minutos abierto.
      const res = await api.get(`/research/sar/${sar.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${sar.code || 'SAR'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // El interceptor axios ya redirige al login en 401. Aquí sólo mostramos el detalle.
      let detail = e.message;
      if (e.response) {
        detail = `HTTP ${e.response.status}`;
        // El detail viene como blob → hay que leerlo
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j.detail) detail = j.detail;
        } catch { /* body no JSON */ }
      }
      alert('Error descargando PDF: ' + detail);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="sar-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">SAR · SITE ACQUISITION REPORT</div>
            <h2 className="text-2xl font-black text-white mt-1">{sar.code}</h2>
            <div className="text-white/40 text-xs mt-1">Generado {new Date(sar.generated_at).toLocaleString('es-ES')} · {sar.lat?.toFixed(5)}, {sar.lng?.toFixed(5)}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setSigning(true)} data-testid="btn-sar-sign"
              className="flex items-center gap-2 bg-white/5 border border-white/10 text-white font-black px-3 py-2 rounded-xl hover:bg-white/10 text-sm">
              <PenLine size={14}/>
              {sar.technician_name ? 'Editar firma' : 'Firmar'}
            </button>
            <button onClick={downloadPdf} data-testid="btn-sar-pdf"
              className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-3 py-2 rounded-xl hover:bg-[#b8ff3a] text-sm">
              <Download size={14}/> PDF
            </button>
            <button onClick={onClose} className="text-white/60 hover:text-white p-2" aria-label="Cerrar"><X size={18}/></button>
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {sar.resumen_ejecutivo && (
            <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/30 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2"><ShieldCheck className="text-[#A3FF12]" size={16}/><b className="text-white text-sm">Resumen ejecutivo</b></div>
                {sar.afecciones_source && sar.afecciones_source !== 'none' && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#A3FF12] bg-black/30 border border-[#A3FF12]/30 rounded-full px-2 py-0.5">
                    Afecciones {sar.afecciones_source === 'provided' ? 'del análisis' : 'del site'}
                  </span>
                )}
              </div>
              <div className="text-white/80 text-sm">{sar.resumen_ejecutivo}</div>
            </div>
          )}

          <Section title="1 · Localización del candidato">
            <Row k="Dirección" v={s1.direccion}/>
            <Row k="Municipio" v={s1.municipio}/>
            <Row k="Provincia" v={s1.provincia}/>
            <Row k="Latitud" v={s1.latitud_dms}/>
            <Row k="Longitud" v={s1.longitud_dms}/>
            <Row k="Código postal" v={s1.codigo_postal}/>
          </Section>

          {/* Catastro (con botón de reintento cuando falta) */}
          <Section title="1.c · Datos catastrales oficiales">
            {sar.catastro && sar.catastro.reference ? (
              <div className="space-y-1">
                <Row k="Referencia" v={sar.catastro.reference}/>
                <Row k="Dirección catastral" v={sar.catastro.address}/>
                <Row k="Superficie" v={sar.catastro.surface_m2 ? `${sar.catastro.surface_m2} m²` : null}/>
                <Row k="Año construcción" v={sar.catastro.year_built}/>
                <Row k="Uso" v={sar.catastro.use}/>
                {sar.catastro.map_png_b64 && (
                  <div className="mt-2">
                    <img alt="mapa catastral"
                      src={`data:image/png;base64,${sar.catastro.map_png_b64}`}
                      className="w-full max-w-xs rounded border border-white/10"/>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-orange-300 text-xs font-black mb-1">Catastro no disponible</div>
                    <div className="text-white/70 text-xs">
                      {sar.catastro?.error || 'La SEDE del Catastro no respondió al generar el SAR. Puedes reintentar sin regenerar el informe.'}
                    </div>
                  </div>
                  <button onClick={refreshCatastro} disabled={refreshingCat}
                    data-testid="btn-refresh-catastro"
                    className="flex-shrink-0 bg-[#A3FF12] text-black font-black text-[11px] px-3 py-1.5 rounded-lg hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center gap-1">
                    {refreshingCat ? <><Loader2 className="animate-spin" size={11}/> Consultando…</> : 'Reintentar Catastro'}
                  </button>
                </div>
              </div>
            )}
          </Section>

          <Section title="2 · Adquisición del emplazamiento">
            <Row k="Uso del terreno" v={s2.uso_terreno}/>
            <Row k="Calificación" v={s2.calificacion}/>
            <Row k="Especial protección" v={s2.especial_proteccion}/>
            <Row k="Desplazar ubicación" v={s2.posibilidad_desplazar}/>
            <Row k="Tipo emplazamiento" v={s2.tipo_emplazamiento}/>
            <Row k="Altura estimada" v={s2.altura_estimacion_m ? `${s2.altura_estimacion_m} m` : ''}/>
            <Row k="Tipo propiedad" v={s2.tipo_propiedad}/>
            <Row k="Acceso 24/7" v={s2.acceso_247}/>
            <Row k="Detalles acceso" v={s2.detalles_acceso}/>
            <Row k="Tipo instalación" v={s2.tipo_instalacion}/>
            <Row k="Comentarios" v={s2.comentarios_instalacion}/>
          </Section>

          <Section title="3 · Transmisión">
            <Row k="Fibra óptica" v={s3.fibra_disponibilidad}/>
            <Row k="Distancia fibra" v={s3.fibra_distancia_m ? `${s3.fibra_distancia_m} m` : ''}/>
            <Row k="Radio enlace" v={s3.radio_enlace_visibilidad}/>
          </Section>

          <Section title="4 · Obra civil y arquitectura">
            <Row k="Camino acceso" v={s4.camino_acceso}/>
            <Row k="Tipo terreno" v={s4.tipo_terreno}/>
            <Row k="Distancia acondicionar" v={s4.distancia_acondicionar_m ? `${s4.distancia_acondicionar_m} m` : ''}/>
            <Row k="Todoterreno" v={s4.necesidad_todoterreno}/>
            <Row k="Año construcción" v={s4.edificio_ano_construccion}/>
            <Row k="Altura edificio" v={s4.edificio_altura_m ? `${s4.edificio_altura_m} m` : ''}/>
            <Row k="Nº plantas" v={s4.edificio_num_plantas}/>
            <Row k="Cubierta plana" v={s4.edificio_cubierta_plana}/>
            <Row k="Material cubierta" v={s4.edificio_material_cubierta}/>
            <Row k="Acceso azotea" v={s4.acceso_azotea}/>
          </Section>

          <Section title="5 · Legalizar · Permisos requeridos">
            <table className="w-full text-xs">
              <thead className="text-white/50 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-2 py-1">Permiso</th>
                  <th className="text-left px-2 py-1">Administración</th>
                  <th className="text-center px-2 py-1">Requerido</th>
                  <th className="text-left px-2 py-1">Tramitación</th>
                </tr>
              </thead>
              <tbody>
                {(s5.permisos_requeridos || []).map((p, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-2 py-1.5 text-white">{p.permiso}</td>
                    <td className="px-2 py-1.5 text-white/60">{p.administracion || '—'}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${
                        (p.requerido || '').toLowerCase().startsWith('s')
                          ? 'bg-orange-500/20 text-orange-300'
                          : 'bg-white/5 text-white/40'
                      }`}>{p.requerido || '—'}</span>
                    </td>
                    <td className="px-2 py-1.5 text-white/70">{p.tramitacion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 pt-3 border-t border-white/5">
              <Row k="Tipo suelo" v={cu.tipo_suelo}/>
              <Row k="Protección" v={cu.condiciones_proteccion}/>
              <Row k="Altura máxima" v={cu.altura_maxima}/>
              <Row k="Retranqueos" v={cu.retranqueos}/>
              <Row k="Mimetización" v={cu.exige_mimetizacion}/>
              <Row k="Normativa aplicable" v={s5.normativa_aplicable}/>
            </div>
          </Section>

          <UrbanisticSection sar={sar} onRefreshed={setSar}/>

          <Section title="6 · Situación zona trabajada">
            <Row k="Ciudad/Municipio" v={s6.ciudad_municipio}/>
            <Row k="Barrio" v={s6.barrio}/>
            <Row k="Morfología" v={s6.morfologia}/>
            <Row k="Detalles" v={s6.detalles_zona}/>
            <Row k="Problemas" v={s6.problemas_zona}/>
          </Section>

          <Section title="7 · Simulaciones">
            <Row k="Entorno" v={s7.entorno_simulado}/>
            <Row k="Coberturas objetivo" v={s7.coberturas_objetivo}/>
            <Row k="Nota" v={s7.nota}/>
          </Section>

          <Section title="8 · Informe fotográfico">
            {/* Uploader por categoría */}
            <div className="grid sm:grid-cols-2 gap-3">
              {(categories.length ? categories : (s8.checklist || []).map((l, i) => ({ id: `chk-${i}`, label: l })))
                .map((cat) => {
                  const catPhotos = photos.filter((p) => p.category === cat.id);
                  return (
                    <div key={cat.id} className="bg-black/30 border border-white/10 rounded-xl p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-white text-xs font-bold flex items-center gap-1.5">
                          <Camera size={13} className="text-[#A3FF12]"/> {cat.label}
                        </div>
                        <span className="text-white/40 text-[10px]">{catPhotos.length} {catPhotos.length === 1 ? 'foto' : 'fotos'}</span>
                      </div>
                      {catPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {catPhotos.map((p) => (
                            <div key={p.id} className="relative group">
                              {p.url
                                ? <img src={p.url} alt={p.caption || cat.label} className="w-16 h-16 object-cover rounded-lg border border-white/10"/>
                                : <div className="w-16 h-16 bg-black/50 border border-white/10 rounded-lg flex items-center justify-center"><ImageIcon size={16} className="text-white/40"/></div>}
                              <button onClick={() => deletePhoto(p.id)} data-testid={`del-photo-${p.id}`}
                                className="absolute -top-1 -right-1 bg-red-500/90 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                                <Trash2 size={10}/>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] font-bold text-[#A3FF12] hover:underline">
                        <input type="file" accept="image/*" className="hidden"
                          data-testid={`upload-${cat.id}`}
                          disabled={uploading === cat.id}
                          onChange={(e) => uploadPhoto(cat.id, e.target.files?.[0])}
                          onClick={(e) => { e.target.value = ''; }}/>
                        {uploading === cat.id
                          ? <><Loader2 className="animate-spin" size={12}/> Subiendo…</>
                          : <><Camera size={12}/> Añadir foto</>}
                      </label>
                    </div>
                  );
                })}
            </div>
          </Section>

          {/* Operador (branding del PDF) */}
          <Section title="Operador (branding del PDF)">
            {sar.operator ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="px-3 py-1.5 rounded font-black text-[11px] tracking-wide"
                    style={{ backgroundColor: sar.operator.color || '#0A0A0A', color: sar.operator.text_color || '#FFFFFF' }}
                    data-testid="sar-current-operator">
                    {sar.operator.name || sar.operator.id?.replace('_', ' ') || 'OPERADOR'}
                  </div>
                  <div className="text-white/50 text-[11px]">Aparecerá en la cabecera del PDF.</div>
                </div>
                <button onClick={() => setEditingOperator((s) => !s)}
                  data-testid="sar-edit-operator"
                  className="text-[11px] font-bold text-[#A3FF12] hover:underline">
                  {editingOperator ? 'Cerrar' : 'Cambiar'}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="text-white/50 text-xs italic">Sin operador asignado.</div>
                <button onClick={() => setEditingOperator(true)}
                  data-testid="sar-set-operator"
                  className="text-[11px] font-bold text-[#A3FF12] hover:underline">
                  Elegir operador
                </button>
              </div>
            )}
            {editingOperator && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid="sar-operator-editor">
                <OperatorSelect value={sar.operator} onChange={(op) => saveOperator(op)}/>
                {operatorSaving && <div className="text-white/50 text-[10px] flex items-center gap-1"><Loader2 className="animate-spin" size={10}/> Guardando…</div>}
              </div>
            )}
          </Section>

          {/* Firma técnico */}
          <Section title="Firma técnico">
            {sar.technician_name ? (
              <div className="bg-black/40 border border-[#A3FF12]/30 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white font-bold text-sm">{sar.technician_name}</div>
                    <div className="text-white/50 text-[11px]">Firmado {sar.technician_signed_at?.slice(0, 10)}</div>
                  </div>
                  {sar.technician_signature_b64 && (
                    <img alt="firma" src={`data:image/png;base64,${sar.technician_signature_b64}`}
                      className="h-14 bg-white rounded px-2"/>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-white/50 text-xs italic">Sin firma. Pulsa <b className="text-white">Firmar</b> arriba para cerrar el informe en visita.</div>
            )}
          </Section>
        </div>
      </div>
      {signing && (
        <SARSignatureModal sarId={sar.id} initialName={sar.technician_name || ''}
          onClose={() => setSigning(false)}
          onSigned={(updated) => setSar(updated)}/>
      )}
    </div>
  );
};

/**
 * Botón que dispara la generación del SAR y muestra el modal.
 * Ahora usa el flujo asíncrono (`POST /api/research/sar/async`) con polling y barra
 * de progreso, dejando la UI utilizable durante los ~40s de Gemini.
 * Props: { lat, lng, address, height_m, site_id, site_name, variant? }
 */
const SARButton = ({ lat, lng, address, height_m, site_id, site_name, afecciones_report, className = '', variant = 'primary' }) => {
  const [job, setJob] = useState(null); // {job_id, status, progress, stage}
  const [sar, setSar] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [operator, setOperator] = useState(null);
  const [equipmentLocation, setEquipmentLocation] = useState('outdoor');

  const loading = job && ['queued', 'running'].includes(job.status);
  const errored = job && job.status === 'error';

  const startJob = async () => {
    try {
      const payload = { lat, lng, address, height_m, site_id, site_name };
      if (afecciones_report) payload.afecciones_report = afecciones_report;
      if (operator) payload.operator = operator;
      if (equipmentLocation) payload.equipment_location = equipmentLocation;
      const { data } = await api.post('/research/sar/async', payload);
      setJob({ job_id: data.job_id, status: data.status, progress: data.progress || 0, stage: data.stage || 'En cola…' });
    } catch (e) {
      alert('Error iniciando SAR: ' + (e.response?.data?.detail || e.message));
    }
  };

  const openPicker = () => {
    if (loading || !lat || !lng) return;
    setPickerOpen(true);
  };

  const confirmAndStart = async () => {
    setPickerOpen(false);
    await startJob();
  };

  const retry = async () => {
    // Si el backend conoce el job, pide reintento; si falla (job desaparecido), arranca uno nuevo.
    if (!job?.job_id) return startJob();
    try {
      const { data } = await api.post(`/research/sar/jobs/${job.job_id}/retry`);
      setJob({ job_id: data.job_id, status: data.status, progress: data.progress || 0, stage: data.stage || 'En cola…' });
    } catch {
      await startJob();
    }
  };

  // Poll job status — bail after 6 consecutive errors to avoid runaway on 401/403
  useEffect(() => {
    if (!job || !['queued', 'running'].includes(job.status)) return;
    let cancelled = false;
    let errors = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/research/sar/jobs/${job.job_id}`);
        if (cancelled) return;
        errors = 0;
        // Preserva job_id aunque el backend devuelva el doc de Mongo con `id` en su lugar
        // (fix contrato: get devuelve `id`, POST /async devuelve `job_id`).
        setJob({ ...data, job_id: data.job_id || data.id || job.job_id });
        if (data.status === 'done' && data.sar) {
          setSar(data.sar);
        }
      } catch (err) {
        errors += 1;
        if (errors >= 6) {
          setJob((j) => j ? { ...j, status: 'error', error: 'Sin respuesta del servidor' } : j);
        }
      }
    };
    const iv = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [job?.job_id, job?.status]);

  const base = 'flex items-center gap-2 font-black rounded-xl text-sm transition-colors disabled:opacity-40';
  const styles = variant === 'primary'
    ? 'bg-[#A3FF12] text-black hover:bg-[#b8ff3a] px-4 py-2'
    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10 px-3 py-2';

  const closeProgress = () => setJob(null);

  return (
    <>
      <button onClick={openPicker} disabled={loading} data-testid="btn-generate-sar"
        className={`${base} ${styles} ${className}`}>
        {loading ? <Loader2 className="animate-spin" size={14}/> : <FileText size={14}/>}
        {loading ? `Generando SAR… ${job.progress || 0}%` : 'Generar SAR'}
      </button>
      {pickerOpen && (
        <SAROperatorPickerModal
          operator={operator}
          onChange={setOperator}
          equipmentLocation={equipmentLocation}
          onEquipmentLocationChange={setEquipmentLocation}
          onCancel={() => setPickerOpen(false)}
          onConfirm={confirmAndStart}/>
      )}
      {loading && (
        <SARProgressBadge job={job} onClose={closeProgress}/>
      )}
      {errored && (
        <SARErrorBadge job={job} onRetry={retry} onClose={closeProgress}/>
      )}
      {sar && <SARModal sar={sar} onClose={() => { setSar(null); setJob(null); }}/>}
    </>
  );
};

/**
 * Modal ligero para elegir operador ANTES de arrancar el job SAR.
 * Se renderiza vía Portal para no quedar atrapado en overlays con transform.
 * El operador es OBLIGATORIO: sin operador el botón "Generar SAR" queda deshabilitado.
 */
const SAROperatorPickerModal = ({ operator, onChange, equipmentLocation, onEquipmentLocationChange, onCancel, onConfirm }) => {
  if (typeof document === 'undefined') return null;
  const hasOperator = !!(operator?.id || operator?.name);
  return createPortal(
    <div className="fixed inset-0 z-[10060] bg-black/80 backdrop-blur flex items-center justify-center p-4"
      onClick={onCancel} data-testid="sar-operator-picker">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">GENERAR SAR</div>
            <div className="text-white font-black text-lg mt-1 flex items-center gap-2">
              <Building size={16}/> Elige el operador
            </div>
            <p className="text-white/50 text-xs mt-1">Es obligatorio para arrancar el SAR. El logo del operador aparecerá en la cabecera del PDF.</p>
          </div>
          <button onClick={onCancel} className="text-white/40 hover:text-white p-1" data-testid="sar-operator-picker-close"><X size={20}/></button>
        </div>
        <OperatorSelect value={operator} onChange={onChange}/>

        {/* Selector ubicación de equipos: Indoor / Outdoor / Sala habilitada */}
        <div className="pt-2 border-t border-white/10">
          <div className="text-[10px] font-black tracking-widest text-white/60 mb-2">UBICACIÓN DE LOS EQUIPOS</div>
          <div className="grid grid-cols-3 gap-2" data-testid="sar-equipment-location">
            {[
              { id: 'outdoor',         label: 'Outdoor',         desc: 'A la intemperie · cubierta / sin caseta' },
              { id: 'indoor',          label: 'Indoor',          desc: 'Caseta o habitación a construir' },
              { id: 'sala_habilitada', label: 'Sala habilitada', desc: 'Habitación existente del edificio' },
            ].map((opt) => (
              <button key={opt.id}
                type="button"
                onClick={() => onEquipmentLocationChange(opt.id)}
                data-testid={`sar-eq-loc-${opt.id}`}
                className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${equipmentLocation === opt.id
                  ? 'bg-[#A3FF12]/10 border-[#A3FF12]/50 text-white'
                  : 'bg-white/5 border-white/10 text-white/70 hover:border-white/25'}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full border-2 ${equipmentLocation === opt.id ? 'border-[#A3FF12] bg-[#A3FF12]' : 'border-white/40'}`}/>
                  <span className="font-black text-xs">{opt.label}</span>
                </div>
                <div className="text-[10px] text-white/50 mt-1 leading-tight">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel}
            className="text-white/60 hover:text-white text-xs font-bold px-3 py-2">
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={!hasOperator}
            data-testid="sar-operator-confirm"
            className="bg-[#A3FF12] text-black font-black text-sm px-4 py-2 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
            <FileText size={13}/> {hasOperator ? 'Generar SAR' : 'Elige un operador'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Flotante inferior-derecha que muestra el progreso del job SAR en marcha.
 * Se renderiza vía React Portal a document.body para escapar de cualquier
 * ancestro con `transform`/`filter` (evita el bug del badge en la esquina
 * superior derecha del modal padre).
 */
const SARProgressBadge = ({ job, onClose }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[10050] bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-2xl shadow-2xl p-4 w-80 max-w-[calc(100vw-2rem)]"
      data-testid="sar-progress-badge">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Loader2 className="animate-spin text-[#A3FF12]" size={14}/>
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">GENERANDO SAR</div>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white text-xs" title="Ocultar">−</button>
      </div>
      <div className="text-white text-xs mb-2 min-h-[16px]" data-testid="sar-progress-stage">{job.stage || 'Trabajando…'}</div>
      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-[#A3FF12] transition-all" style={{ width: `${Math.max(3, job.progress || 0)}%` }} data-testid="sar-progress-bar"/>
      </div>
      <div className="text-white/40 text-[10px] mt-1 text-right">{job.progress || 0}%</div>
    </div>,
    document.body,
  );
};

/**
 * Badge de error con botón "Reintentar". Se muestra si el job termina en error
 * (incluye el caso "job atascado" auto-detectado por el watchdog del backend
 * cuando lleva > 5 min sin heartbeat).
 */
const SARErrorBadge = ({ job, onRetry, onClose }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[10050] bg-[#0a0a0a] border border-red-500/40 rounded-2xl shadow-2xl p-4 w-80 max-w-[calc(100vw-2rem)]"
      data-testid="sar-error-badge">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] font-black tracking-widest text-red-300">SAR NO COMPLETADO</div>
        <button onClick={onClose} className="text-white/40 hover:text-white text-xs" title="Cerrar">×</button>
      </div>
      <div className="text-white/80 text-xs mb-3" data-testid="sar-error-msg">
        {job.error || 'La generación no pudo completarse.'}
      </div>
      <button onClick={onRetry}
        data-testid="sar-retry-btn"
        className="w-full bg-[#A3FF12] text-black font-black py-2 rounded-lg text-sm hover:bg-[#b8ff3a]">
        Reintentar
      </button>
    </div>,
    document.body,
  );
};

export default SARButton;
export { SARModal };
